import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { useShellTitle } from '../components/layout/ShellTitleContext';
import Spinner from '../components/ui/Spinner';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import { LearnerRecordView } from '../features/learnerRecord';
import { getMyRecord } from '../features/learnerRecord/learnerRecordApi';
import type { LearnerRecordDto } from '../features/learnerRecord/types';

// The same underlying facts as the instructor's export — see effortSummary()
// below — described the way the instructor would want a student to read
// them: "here's what shows you're putting in effort", not "here's what you
// haven't done yet". Never invents a second definition of participation —
// every number here already exists in `record.first_hand` (BP 27), just
// composed into one sentence instead of five separate stat cards.
function effortSummary(record: LearnerRecordDto): string {
  const { lessons, engagement, participation, attendance } = record.first_hand;
  const nothingYet =
    engagement.logins === 0 && lessons.rows.length === 0 && participation.responded === 0 && attendance.present === 0;

  if (nothingYet) {
    return "Nothing recorded yet — once you log in, open a lesson, or check in to class, it shows up here automatically. There's no catching up to do; it just starts counting from wherever you begin.";
  }

  const parts: string[] = [];
  parts.push(`you've logged in ${engagement.logins} time${engagement.logins === 1 ? '' : 's'}`);
  if (lessons.total > 0) {
    parts.push(`opened ${lessons.rows.length} of ${lessons.total} lesson${lessons.total === 1 ? '' : 's'}`);
  }
  if (lessons.xp > 0) parts.push(`earned ${lessons.xp} XP`);
  if (attendance.eligible > 0) parts.push(`attended ${attendance.present} of ${attendance.eligible} classes`);

  const facts = parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0];
  return `So far ${facts}. This record updates automatically as you keep going — nothing to submit, nothing to remember to do.`;
}

// /me/record — BP 27, "transparency by default": a student's own evidence
// file, including instructor notes, rendered exactly the way an instructor
// would see it (LearnerRecordView is the same component either way, mode
// only changes which write controls appear). What differs here is framing:
// the instructor's page is an audit document; this one leads with a plain,
// honest summary of effort so far — encouraging by being accurate, never by
// hiding a low number, and never scolding one either.
export default function MyRecordPage() {
  useShellTitle('My record');
  const [record, setRecord] = useState<LearnerRecordDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    setError(null);
    getMyRecord()
      .then(setRecord)
      .catch(() => setError('Could not load your record.'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  return (
    <div className="ws-page space-y-4">
      <header>
        <h1 className="text-xl font-semibold text-ws-ink">My record</h1>
        <p className="mt-1 max-w-3xl text-sm text-ws-ink-3">
          Everything ACE has evidenced about your attendance, participation and progress, plus a
          separately labelled copy of your D2L status. This is exactly what your instructor sees.
        </p>
      </header>

      {loading ? (
        <Spinner label="Loading your record…" />
      ) : error ? (
        <div className="rounded-lg border border-ws-border bg-ws-surface p-6 text-center">
          <p className="text-sm text-ws-danger">{error}</p>
          <Button className="mt-3" variant="secondary" size="sm" onClick={load}>Retry</Button>
        </div>
      ) : record ? (
        <>
          <Card className="flex items-start gap-3 p-4">
            <Sparkles size={16} className="mt-0.5 shrink-0 text-ws-accent" aria-hidden />
            <p className="text-sm text-ws-ink-2">{effortSummary(record)}</p>
          </Card>
          <LearnerRecordView record={record} mode="student" />
        </>
      ) : null}
    </div>
  );
}
